import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BellRing, Check } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/format";

const KEY = "perfero_stock_alerts";

const readSubs = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const Done = ({ email, compact }) => (
  <p className={`flex items-start gap-3 text-navy ${compact ? "text-[14px]" : "text-[16px]"}`} role="status">
    <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#166534]" />
    <span>Fatto! Ti scriveremo a <strong className="break-all">{email}</strong> appena torna disponibile.</span>
  </p>
);

export const NotifyForm = ({ product, compact = false }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(() => readSubs()[product.slug] || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const id = `notify-${compact ? "card" : "page"}-${product.slug}`;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post(`/products/${product.slug}/notify`, { email });
      localStorage.setItem(KEY, JSON.stringify({ ...readSubs(), [product.slug]: email.trim() }));
      setDone(email.trim());
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const wrap = compact ? "mt-4" : "mt-6 rounded-3xl border border-copper/25 bg-cream p-6 sm:p-7";
  if (done) return <div className={wrap} data-testid={`notify-done-${product.slug}`}><Done email={done} compact={compact} /></div>;

  return (
    <form onSubmit={submit} className={wrap} data-testid={`notify-form-${product.slug}`} noValidate={false}>
      {!compact && (
        <>
          <p className="flex items-center gap-3 font-display text-2xl text-navy"><BellRing className="h-5 w-5 text-copper" strokeWidth={1.5} /> Avvisami quando torna disponibile</p>
          <p className="mt-2 text-[16px] leading-relaxed text-slate2">Lascia la tua email: ti scriveremo una sola volta, appena {product.name} sarà di nuovo in vendita.</p>
        </>
      )}
      <label htmlFor={id} className={compact ? "text-[14px] font-medium text-navy" : "sr-only"}>Avvisami quando torna</label>
      <div className={`flex items-center gap-2 rounded-full border border-navy/20 bg-ivory p-1 focus-within:border-copper ${compact ? "mt-2" : "mt-5"}`}>
        <input id={id} type="email" required autoComplete="email" inputMode="email" placeholder="La tua email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`notify-email-${product.slug}`} className={`w-0 min-w-0 flex-1 bg-transparent pl-4 text-navy outline-none placeholder:text-slate2 ${compact ? "h-10 text-[15px]" : "h-12 text-[17px]"}`} />
        <button type="submit" disabled={loading} aria-label="Avvisami" data-testid={`notify-submit-${product.slug}`} className={`grid shrink-0 place-items-center rounded-full bg-navy text-ivory transition-colors hover:bg-copper-deep disabled:opacity-60 ${compact ? "h-10 w-10" : "h-12 px-6 text-[16px] font-medium"}`}>
          {compact ? <ArrowRight className="h-4 w-4" /> : loading ? "Invio…" : "Avvisami"}
        </button>
      </div>
      {error && <p role="alert" className="mt-2 text-[14px] text-destructive" data-testid={`notify-error-${product.slug}`}>{error}</p>}
      <p className="mt-2 text-[13px] leading-snug text-slate2">
        Una sola email quando torna disponibile, nessuna pubblicità.{" "}
        <Link to="/privacy" className="text-navy underline underline-offset-2" data-testid={`notify-privacy-${compact ? "card" : "page"}-${product.slug}`}>Privacy</Link>
      </p>
    </form>
  );
};
